import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive'
import { I18nextProvider } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import genericStyles from './GenericComponents/GenericStyles.module.css';
import styles from './App.module.css';
import { CCProvider, useCCContext } from './CC/CCContext';
import './CC/CC';
import Startup from './Startup';
import User from './User/User';
import Mobile from './Mobile/Mobile';
import Header from './Header/Header';
import HeaderMobile from './Header/HeaderMobile';
import Footer from './Footer/Footer';
import SimpleSearchBox from './Search/SimpleSearchBox';
import Filter from './Filter/Filter';
import Facet from './Facet/Facet';
import HierarchicalFacet from './Facet/HierarchicalFacet';
import Navigator from './Navigator/Navigator';
import ViewMode from './Navigator/ViewMode';
import BrowseMode from './Navigator/BrowseMode';
import ResultSet from './Result/ResultSet';
import Map from './Record/Map';
import QuerySummary from './Result/QuerySummary';
import Sort from './Navigator/Sort';
import EmbedCount from './Embed/EmbedCount';
import EmbedFacet from './Embed/EmbedFacet';
import EmbedArticle from './Embed/EmbedArticle';
import DebugCC from './Diagnostics/DebugCC';
import RightClickPopup from './GenericComponents/RightClickPopup';
import HomepageBlock from './HomePageblock';
import GenericIconButton from './GenericComponents/GenericIconButton';

import { ccConfig, CC, Search } from './CC/CC';
import * as ccUtils from './CC/utils';
import ArchiveCount from './MQB/ArchiveCount';
import BiblioCount from './MQB/BiblioCount';

function MainContent() {
  const { t } = useTranslation();
  const { cc, setCc, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData, isReady } = useCCContext();
  const ctx = useCCContext();
  const [debugOpen, setDebugOpen] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  //embed stuff
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const embedType = params.get('embed');
  const facetValue = params.get('facet');
  const articleId = params.get('articleId');
  const state = params.get('state');

  const isDesktopOrLaptop = useMediaQuery({
    query: '(min-width: 1224px)'
  })
  const isBigScreen = useMediaQuery({ query: '(min-width: 1824px)' })
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' })
  const isRetina = useMediaQuery({ query: '(min-resolution: 2dppx)' })
  //const isTabletOrMobile = false;//useMediaQuery({ query: '(max-width: 1024px)' })
  const queryParams = new URLSearchParams(window.location.search);

  const isTabletOrMobile = window.innerWidth < 1080 || queryParams.get('tablet') === 'true' || queryParams.get('mobile') === 'true';

  const handleDebugOpen = () => {
    setDebugOpen(true);
  };

  const handleDebugClose = () => {
    setDebugOpen(false);
  };

  const handleAddCustomSearch = () => {
    ccUtils.addCustomSearch(ctx, '*', 'All fields', ['masque', 'voiture'], '');
    console.log('Custom search added:', cc.customSearchCriteria);
  };

  const handleCustomSearch = () => {
    ccUtils.customSearch(ctx, '/Record/Title', 'Title', 'masque', 'Searching for Masque');
    console.log('Custom search set:', cc.customSearchCriteria);
  };

  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScrollToTop && window.scrollY > 2000) {
        setShowScrollToTop(true);
      } else if (showScrollToTop && window.scrollY <= 2000) {
        setShowScrollToTop(false);
      }
    };

    window.addEventListener('scroll', checkScrollTop);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener('scroll', checkScrollTop);
  }, [showScrollToTop]);

  if (!isReady) {
    return <div></div>; // or return null to render nothing
  }

  //http://localhost:3000/?embed=count&state={%22Searches%22:[{%22identifier%22:%22simpleSearch%22,%22field%22:%22All%20search%20fields%22,%22searchValues%22:[{%22source%22:%22typeahead%22,%22text%22:%22masque%22}]}],%22matchAllCriteria%22:false}
  //http://localhost:3000/?embed=count&state={"Searches":[{"identifier":"simpleSearch","field":"All search fields"}],"matchAllCriteria":false}

  if (embedType === 'count') {
    return <EmbedCount state={state} ccContext={ctx} />;
  }

  //http://localhost:3000/?embed=facet&facet=categorie
  if (embedType === 'facet' && facetValue) {
    return <EmbedFacet facetName={facetValue} />
  }

  //http://localhost:3000/?embed=article&articleId=160d5d4e-7c5a-4b84-b81e-47b577b58395
  if (embedType === 'article' && articleId) {
    return <EmbedArticle articleId={articleId} ccContext={ctx} />
  }

  const introTextEnglish = 'Access the complete collections of objects and photography collections, nearly a million illustrated records, as well as descriptive catalogs of all collections of books, magazines, films, music, and archival collections.';
  const introTextFrench = 'Accédez à la totalité des collections d’objets et aux collections de photographies, soit près d’un million de notices illustrées, ainsi qu’aux catalogues descriptifs de toutes les collections de livres, revues, films, musiques et des fonds d’archives.';

  const expandedTextEnglish = 'These various collections are enriched and updated as acquisitions are made and research on the collections progresses: history, provenance, technical analyses, etc. Some records may contain sensitive content.\nThis new portal, still under development, provides access to more information than the previous one. It will be updated regularly to improve its functionality.';
  const expandedTextFrench = 'Ces différents ensembles sont enrichis et corrigés au fur et à mesure des acquisitions comme de l’avancée des travaux de recherche sur les collections : histoire, provenance, analyses techniques etc.\nCertaines notices peuvent comporter des contenus sensibles.\nCe nouveau portail, encore en cours de développement, donne accès à davantage d’informations que le précédent. Il sera mis à jour régulièrement pour en améliorer les fonctionnalités.';

  return (
    <I18nextProvider i18n={i18n}>
      {isTabletOrMobile ? (
        <Mobile></Mobile>
      ) : (
        <div className={styles.App}>
          <Header />
          <div style={{ 'display': 'none' }}>
            <div onClick={handleAddCustomSearch} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Click me to add custom search
            </div>
            <div onClick={handleCustomSearch} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Click me to set custom search
            </div>
            <div onClick={() => ccUtils.activateFilter(ctx, "objects")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Activate Object Filter
            </div>
            <div onClick={() => ccUtils.deactivateFilter(ctx, "objects")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Deactivate Object Filter
            </div>
            <div onClick={() => ccUtils.clearFacet(ctx, "words")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Clear Words Facet
            </div>
            <div onClick={() => ccUtils.addFacetValue(ctx, "words", "bois")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Add New Word to Words Facet
            </div>
            <div onClick={() => ccUtils.addFacetValue(ctx, "words", ["bois", "voiture"])} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Add New Words to Words Facet
            </div>
            <div onClick={() => ccUtils.clearSearch(ctx)} style={{ padding: '20px', margin: '10px', border: '1px solid black', cursor: 'pointer' }}>
              Clear Search
            </div>
          </div>

          <div className={styles.debugLink} onClick={handleDebugOpen}>
            <RightClickPopup url="https://example.com/record">Debug</RightClickPopup></div>

          <div className={styles.container}>
            <div className={styles.middlePane}>
              <div className={styles.bottomMiddlePane}>
                <div className={styles.leftColumn}>
                  <div className={styles.filters}>
                    <div className={styles.objetsIcono}>
                      <Filter filterName="filter_objects" />
                      <Filter filterName="filter_icono" />
                      <Filter filterName="filter_freeOfRights" />
                      <Filter filterName="filter_exhibited" />
                    </div>
                  </div>

                  <div className={styles.facets}>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['categorie'] && cc.facets['categorie'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_category")}</span>
                        {cc.facets['categorie'] && cc.facets['categorie'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='categorie' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['personnes'] && cc.facets['personnes'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_persons")}</span>
                        {cc.facets['personnes'] && cc.facets['personnes'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='personnes' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.hierarchicalFacets['culture'] && cc.hierarchicalFacets['culture'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_culture")}</span>
                        {cc.hierarchicalFacets['culture'] && cc.hierarchicalFacets['culture'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <HierarchicalFacet hierarchicalFacetName='culture' contentHeight='160px' sort='alpha' showSelectedAbove={false} />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.hierarchicalFacets['toponyme'] && cc.hierarchicalFacets['toponyme'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_geography")}</span>
                        {cc.hierarchicalFacets['toponyme'] && cc.hierarchicalFacets['toponyme'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <HierarchicalFacet hierarchicalFacetName='toponyme' contentHeight='160px' sort='alpha' showSelectedAbove={false} />
                      </div>
                    </div>
                  </div>
                </div>

                {viewMode !== 'homepage' ? (
                  <div className={styles.homePageButton}>
                    <GenericIconButton
                      icon='light/house.svg'
                      hoverEnabled={true}
                      width='28px'
                      height='28px'
                      onClick={(e: any) => {
                        e.preventDefault();
                        const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false };
                        const newStateCc = ccUtils.convertStateToCC(newState);
                        setCc(newStateCc);
                        setViewMode('homepage');
                      }}
                    />
                  </div>
                ) : (null)
                }
                <div className={styles.rightColumn}>
                  <div className={styles.searchContainer}>
                    <div className={styles.simpleSearchBox}>
                      <SimpleSearchBox Fields="simple" id="simpleSearch" showSearchButton={true} />
                    </div>

                  </div>

                  {viewMode !== 'homepage' ?
                    <div>
                      {
                        //<div style={{width: '100%', height: '400px', overflow: 'scroll'}}><EmbedArticle articleId={'160d5d4e-7c5a-4b84-b81e-47b577b58395'} ccContext={ctx} /></div>
                      }
                      {viewMode !== "map" &&
                        <div className={styles.displayControl}>
                          <ViewMode />
                          <div className={styles.displayControlSeperator} />
                          <div className={styles.displayControlItem}>
                            <BrowseMode />
                          </div>
                          {browseMode !== 'scroll' &&
                            (<>
                              <div className={styles.displayControlSeperator} />
                              <div className={styles.displayControlItem}>
                                <Navigator />
                              </div>
                            </>
                            )
                          }
                          <Sort />
                        </div>
                      }
                      {viewMode == "map" && (<div style={{ marginTop: '20px', marginBottom: '20px' }}>
                        <ViewMode />
                        <div className={styles.hyperlinkBackToCollection} style={{ "display": "none" }} onClick={(e) => { setViewMode('list') }}>{t("map_return_to_list")}</div>
                      </div>
                      )
                      }
                      <QuerySummary showResultsButton={viewMode == "map"} />
                      <div>
                        <ArchiveCount />
                      </div>
                      <div>
                        <BiblioCount />
                      </div>

                      {viewMode == "map" && (
                        <div className={styles.map}>
                          <Map dzi="https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=images/MQB_CC3V2_02_21031210_PH.dzi" />
                        </div>
                      )
                      }

                      {
                        viewMode !== "map" ? (
                          <>
                            <ResultSet />
                            {browseMode !== 'scroll' && (
                              <div className={styles.navigatorBottom}>
                                <div className={styles.displayControlSeperator} />
                                <div className={styles.displayControlItem}>
                                  <Navigator />
                                </div>
                              </div>
                            )}
                          </>
                        ) : null
                      }

                    </div>
                    :
                    <div>
                      <div className={styles.intro}>
                        <div className={styles.introText}>
                          {i18n.language === 'en' ? introTextEnglish : i18n.language === 'fr' ? introTextFrench : ''}
                        </div>
                        {isExpanded ? (
                          <div className={isExpanded ? `${styles.isExpanded} ${styles.introAdditionalText}` : `${styles.introAdditionalText}`}>
                            {i18n.language === 'en' ? expandedTextEnglish : i18n.language === 'fr' ? expandedTextFrench : ''}
                          </div>
                        ) : null}
                        <div>
                          <a className={styles.introMoreButton} onClick={() => setIsExpanded(!isExpanded)}>
                            {isExpanded ? `- ${t("homepage_less")}` : `+ ${t("homepage_more")}`}
                          </a>
                        </div>
                        <div className={styles.introHyperlink} onClick={(e) => { setViewMode('list') }}>{t("homepage_click_to_view")}</div>
                      </div>
                      <div className={styles.mapParent}>
                        <div style={{ marginBottom: 20 }}>
                          <ViewMode />
                        </div>
                        <QuerySummary showCount={false} showResultsButton={true} />
                        <div className={styles.map}>
                          <Map dzi="https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=images/MQB_CC3V2_02_21031210_PH.dzi" />
                        </div>
                      </div>
                      <div className={styles.homepageBlocks}>
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Textile ou vêtement"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Objets/Images/118/118692.jpg&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_textile")}
                        />
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Arts graphiques"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Objets/Images/184/184559.jpg&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_arts")}
                        />
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Carte postale", "Affiche"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Icono/iconomedia/MQB-P-0002.11_01/PP0147888_01.JPG&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_affiche_et_carte")}
                        />
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Instrument de musique"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Objets/Images/135/135651.jpg&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_instruments")}
                        />
                      </div>
                      <div className={styles.homepageBlocks}>
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Peinture"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Objets/IDENTIFICATION_OBJETS/Photos_YMAGO/200000/207840y.jpg&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_peintures")}
                        />
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Photographie"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Icono/iconotheque/MQB-P-0005.41_01/PF0171035_01.jpg&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_photos")}
                        />
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Sculpture"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Objets/Images/186/186021.jpg&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_sculpture")}
                        />
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false, "facets": { "categorie": ["Objet archéologique"] } };
                            const newStateCc = ccUtils.convertStateToCC(newState);
                            setCc(newStateCc);
                            setViewMode('list');
                          }}
                          imageUrl={"https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=Objets/Images/169/169194.jpg&width=180&height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=ffffff&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_archeology")}
                        />
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
          {debugOpen && <DebugCC onClose={handleDebugClose} />}
          {
            showScrollToTop &&
            <div className={styles.scrollToTop} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              {t("scroll_to_top")}
            </div>
          }
          <Footer />
        </div>
      )
      }
    </I18nextProvider>
  );
}

function App() {
  useEffect(() => {
    console.log('initialize app');
    //handleURLParameters();
  }, []);

  return (
    <Router>
      <CCProvider>
        <MainContent />
      </CCProvider>
    </Router>
  );

}

export default App;
